import { locale } from "@/language/translation";
/* eslint-disable no-console */
import type Keycloak from "keycloak-js";
import { apolloClient, wsLink } from "@/graphql/apollo";
import RefreshWorker from "@/functions/workers/keycloakWorker?worker";
import { captureMessageInSentry } from "@/sentry";
import { languageNames } from "@/language";

// Hardcoded because i18n chunks may not be available when the network drops.
// Record<keyof typeof languageNames, ...> ensures a TS error when a new language is added.
const sessionExpiredLabels: Record<
  keyof typeof languageNames,
  { login: string; logout: string; message: string; header: string }
> = {
  de: {
    login: "Anmelden",
    logout: "Abmelden",
    message: "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.",
    header: "Sitzung abgelaufen",
  },
  de_CH: {
    login: "Anmelden",
    logout: "Abmelden",
    message: "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.",
    header: "Sitzung abgelaufen",
  },
  en: {
    login: "Login",
    logout: "Logout",
    message: "Your session has expired. Please log in again.",
    header: "Session Expired",
  },
  en_GB: {
    login: "Log in",
    logout: "Log out",
    message: "Your session has expired. Please log in again.",
    header: "Session Expired",
  },
  fr: {
    login: "Se connecter",
    logout: "Se déconnecter",
    message: "Votre session a expiré. Veuillez vous reconnecter.",
    header: "Session expirée",
  },
  es: {
    login: "Iniciar sesión",
    logout: "Cerrar sesión",
    message: "Su sesión ha expirado. Por favor, inicie sesión de nuevo.",
    header: "Sesión expirada",
  },
  pt: {
    login: "Entrar",
    logout: "Sair",
    message: "A sua sessão expirou. Por favor, inicie sessão novamente.",
    header: "Sessão expirada",
  },
  hu: {
    login: "Bejelentkezés",
    logout: "Kijelentkezés",
    message: "A munkamenet lejárt. Kérjük, jelentkezzen be újra.",
    header: "Munkamenet lejárt",
  },
  pl: {
    login: "Zaloguj się",
    logout: "Wyloguj się",
    message: "Twoja sesja wygasła. Zaloguj się ponownie.",
    header: "Sesja wygasła",
  },
  it: {
    login: "Accedi",
    logout: "Disconnetti",
    message: "La tua sessione è scaduta. Per favore, accedi di nuovo.",
    header: "Sessione scaduta",
  },
  uk_UA: {
    login: "Увійти",
    logout: "Вийти",
    message: "Ваша сесія закінчилась. Будь ласка, увійдіть знову.",
    header: "Сесія закінчилась",
  },
};
let worker: Worker | undefined = undefined;

let retryAttempt = 0;
let refreshInterval: number | null = null;

export async function startRefreshKeycloakAccessTokenInterval(
  keycloak: Keycloak,
  _forceRestart = false,
) {
  if (window.Worker) {
    console.info(
      "%c[%cLISAAuth%c]: %cInitializing OAuth Refresh Module Service.",
      "color: white;",
      "color: orange; font-weight: bold",
      "color: white;",
      "color: inherit",
    );
    if (worker) {
      worker.terminate();
    }
    worker = new RefreshWorker({
      name: "Keycloak_Refresh_Worker",
    });
    worker.onmessage = (msg) => {
      if (msg.data.msg === "UPDATE_KEYCLOAK_TOKEN") {
        console.info(
          "%c[%cLISAAuth%c]: %cRefresh Authentication.",
          "color: white;",
          "color: orange; font-weight: bold",
          "color: white;",
          "color: inherit",
        );
        if (!keycloak.authenticated || keycloak.token == null) {
          worker?.terminate();
          worker = undefined;
          return;
        }

        const isExpired = keycloak.isTokenExpired(10);
        if (isExpired) {
          if (window.navigator.onLine === false) {
            console.info(
              "%c[%cLISAAuth%c]: %cCurrently offline, skipping token refresh.",
              "color: white;",
              "color: orange; font-weight: bold",
              "color: white;",
              "color: inherit",
            );
            return;
          } else {
            console.info(
              "%c[%cLISAAuth%c]: %cRefreshing Token.",
              "color: white;",
              "color: orange; font-weight: bold",
              "color: white;",
              "color: inherit",
            );
          }
          keycloak
            .updateToken(10)
            .then((result) => {
              if (result) {
                retryAttempt = 0;
              }
              wsLink.client.terminate();
            })
            .catch(() => {
              /**
               * ? We retry this 3 times before showing the login screen.
               */
              if (retryAttempt < 3) {
                console.info(
                  "%c[%cLISAAuth%c]: %cRetry Attempt: %c" + retryAttempt,
                  "color: white;",
                  "color: orange; font-weight: bold",
                  "color: white;",
                  "color: inherit",
                  "color: red; font-weight: bold",
                );
                retryAttempt += 1;
                return;
              } else {
                worker?.terminate();
                apolloClient.stop();
                const localeKey = (locale.value.replace("-", "_") ??
                  "de") as keyof typeof languageNames;
                const labels = sessionExpiredLabels[localeKey] ?? sessionExpiredLabels.en;
                window.$confirm?.require({
                  accept: () => keycloak.login(),
                  reject: () => keycloak.logout(),
                  position: "center",
                  modal: true,
                  acceptLabel: labels.login,
                  rejectLabel: labels.logout,
                  message: labels.message,
                  header: labels.header,
                });
              }
            });
        }
      }
    };
    worker.postMessage({
      msg: "ENABLE_KEYCLOAK_INTERVAL",
    });
  } else {
    captureMessageInSentry("KeycloackRefresher: Worker cannot be initialized");
    console.info(
      "%c[%cLISAAuth%c]: %cInitializing OAuth Refresh Module.",
      "color: white;",
      "color: orange; font-weight: bold",
      "color: white;",
      "color: inherit",
    );
    async function refresh() {
      captureMessageInSentry("KeycloackRefresher: refresed token");
      console.info(
        "%c[%cLISAAuth%c]: %cRefresh Token.",
        "color: white;",
        "color: orange; font-weight: bold",
        "color: white;",
        "color: inherit",
      );
      const isExpired = keycloak.isTokenExpired(70);
      if (isExpired) {
        await keycloak.updateToken(-1);
        wsLink.client.terminate();
      }
    }

    if (refreshInterval) {
      clearInterval(refreshInterval);
    }
    // Token Refresh
    refreshInterval = window.setInterval(refresh, 5000);
    refresh();
  }
}
